import React, { useEffect, useRef } from 'react';
import { useStyles } from './DealerMap.styles';
import { Dealer, IDealerMap } from 'interfaces/dealerLocator';
import { Icon, Icons } from 'components/Global/Icon/Icon';
import { appPalette } from 'theme/App.palette';
import ReactDOMServer from 'react-dom/server';
import { useDispatch } from 'react-redux';
import { setSelectedDealer } from 'redux/slices/dealerSlice';
import TextRender from 'components/Global/TextRender/TextRender';
import { trackEvent } from 'helpers/Analytics';
import {
  LinkButton,
  LinkButtonType,
} from 'components/Global/LinkButton/LinkButton';
import { theme } from 'theme/App.theme';
import { initCustomPinClasses } from 'helpers/MapPushpinClasses';

const mapConstants = {
  DEFAULT_OPTIONS: {
    disableScrollWheelZoom: true,
    enableClickableLogo: false,
    minZoom: 2,
    showCopyright: false,
    showLocateMeButton: false,
    showMapTypeSelector: false,
    showZoomButtons: true,
    zoom: 3,
  },
  PIN_ANCHOR: {
    x: 15,
    y: 36,
  },
  PIN_COLORS: {
    DEFAULT: '#5d6061',
    SELECTED: '#000',
    PREFERRED: '#f30000',
  },
  PIN_TEXT_OFFSET: {
    x: 0,
    y: 5,
  },
};

export function DealerMap({
  dealers,
  coordinatesSetter,
  hideSearchThisAreaButton,
  hideInfobox,
  infoboxActionOnClick,
  labels,
  isEmbedded,
  isCustomPins,
}: IDealerMap): JSX.Element {
  const Microsoft = window.Microsoft;
  const { acura_red, light_grey_6, black, honda_blue, honda_red } =
    appPalette.colors;
  const mapRef: any = useRef();
  const bingMap: any = useRef();
  const infoboxRef: any = useRef();

  const classes = useStyles();
  const isHondaThemed = theme.themeName === 'honda';
  const dispatch = useDispatch(); // redux dispatch

  const handleSelectADealer = (dealer: Dealer) => {
    dispatch(setSelectedDealer(dealer));
  };

  const handleViewDetails = (dealer: Dealer) => {
    infoboxActionOnClick && infoboxActionOnClick(dealer);
  };

  const handleSearchThisArea = () => {
    trackEvent({
      label: `selector:search selected area`,
      category: {
        includePageName: true,
        text: 'selector',
      },
      type: 'selector',
    });

    if (bingMap.current && coordinatesSetter) {
      const bounds = bingMap.current.getBounds();
      coordinatesSetter({
        latitude: bounds.center.latitude,
        longitude: bounds.center.longitude,
      });
    }
  };

  const updatePins = (zoom: number, map: any) => {
    let bestview;
    const locations: string[] = [];
    if (dealers?.length === 0) {
      const mapOptions = {
        zoom: 3,
      };
      map.setView(mapOptions);
      return;
    }

    const icons = {
      anchorX: mapConstants.PIN_ANCHOR.x,
      anchorY: mapConstants.PIN_ANCHOR.y,
      textOffsetX: mapConstants.PIN_TEXT_OFFSET.x,
      textOffsetY: mapConstants.PIN_TEXT_OFFSET.y,
      DEFAULT: isHondaThemed ? honda_blue : light_grey_6,
      PREFERRED: isHondaThemed ? honda_red : acura_red,
      SELECTED: black,
    };
    map.entities.clear(); // removing points

    dealers?.forEach(function addPoint(dealer, i) {
      let color = icons.DEFAULT;
      if (dealer.preferred) {
        color = icons.PREFERRED;
      } else if (dealer.selected) {
        color = icons.SELECTED;
      }
      const icon = ReactDOMServer.renderToStaticMarkup(
        <Icon
          svgProps={{ height: 30, width: 36, fill: color }}
          color={color}
          type={Icons.dealerPin}
        />
      );
      locations[i] = new Microsoft.Maps.Location(
        dealer.latitude,
        dealer.longitude
      );
      const pin = new Microsoft.Maps.Pushpin(locations[i], {
        text: dealer.customMapIndex?.toString() || (i + 1).toString(),
        icon: icon,
        cursor: 'pointer',
        anchor: new Microsoft.Maps.Point(icons.anchorX, icons.anchorY),
        textOffset: new Microsoft.Maps.Point(
          icons.textOffsetX,
          icons.textOffsetY
        ),
      });

      pin.customIndex = i;

      pin.metadata = {
        dealer: dealer,
      };

      //Add a click event handler to the pushpin.
      Microsoft.Maps.Events.addHandler(pin, 'click', pushpinClicked);
      map.entities.push(pin);
    });

    if (infoboxRef.current !== undefined) {
      infoboxRef.current.setOptions({ visible: false }); // hidde infobox if it exists
    }

    //Create an infobox at the center of the map but don't show it.
    infoboxRef.current = new Microsoft.Maps.Infobox(map.getCenter(), {
      visible: false,
    });

    //Assign the infobox to a map instance.
    infoboxRef.current.setMap(map);

    function pushpinClicked(clickEvent: any) {
      infoboxRef.current.setOptions({ visible: false }); // hidde infobox to display it in another place
      //Make sure the infobox has metadata to display.
      if (clickEvent.target.metadata.dealer && !hideInfobox) {
        const bestview = Microsoft.Maps.LocationRect.fromLocations([
          clickEvent.target.getLocation(),
        ]);
        map.setView({ center: bestview.center });

        //Set the infobox options with the metadata of the pushpin.
        infoboxRef.current.setLocation(clickEvent.target.getLocation());
        infoboxRef.current.setOptions({
          visible: true,
          ignoreDelay: true,
          title: clickEvent.target.metadata.dealer.name,
          description: clickEvent.target.metadata.dealer.drivingDistanceMiles
            ? `${clickEvent.target.metadata.dealer.drivingDistanceMiles} ${labels.milesText}`
            : `- ${labels.milesText}`,
          showPointer: true,
          maxWidth: 286,
          offset: new Microsoft.Maps.Point(0, 60),
          actions: [
            {
              label: isEmbedded
                ? labels.selectADealer
                : labels.mapInfoboxLinkText,
              eventHandler: (p: PointerEvent) => {
                isEmbedded
                  ? handleSelectADealer(clickEvent.target.metadata.dealer)
                  : handleViewDetails(clickEvent.target.metadata.dealer);
              },
            },
          ],
        });
      }
    }

    bestview = Microsoft.Maps.LocationRect.fromLocations(locations);

    const mapOptions = {
      center: bestview.center,
      zoom: zoom,
    };
    map.setView(mapOptions);
  };

  const initMap = () => {
    const map = new Microsoft.Maps.Map(mapRef.current);
    map.setOptions(mapConstants.DEFAULT_OPTIONS);
    if (isCustomPins) {
      const { MapPushpin, MapPushpinLayer } = initCustomPinClasses();

      //Create some HTML pushpins in random locations.
      var locs = Microsoft.Maps.TestDataGenerator.getLocations(
        3,
        map.getBounds()
      );

      //Create a simple reusable HTML template to keep things easy.
      var htmlTemplate =
        '<div style="color:red;background-color:white;width:100px;padding:2px;text-align:center;">{text}</div>';

      var pins = [
        new MapPushpin(
          locs[0],
          htmlTemplate.replace('{text}', 'Hello World!'),
          new Microsoft.Maps.Point(50, 12)
        ),
        new MapPushpin(
          locs[1],
          htmlTemplate.replace('{text}', 'My Pushpin'),
          new Microsoft.Maps.Point(50, 12)
        ),
        new MapPushpin(
          locs[2],
          htmlTemplate.replace('{text}', 'HTML Rules!'),
          new Microsoft.Maps.Point(50, 12)
        ),
      ];

      var layer = new MapPushpinLayer();
      layer.setPushpins(pins);

      console.log('layer', layer);

      // //Add the HTML pushpin to the map.
      map.layers.insert(layer);
    }
    return map;
  };

  useEffect(() => {
    if (mapRef.current) {
      if (!bingMap.current) {
        bingMap.current = initMap();
        const zoom = 8;
        updatePins(zoom, bingMap.current);
      } else {
        const zoom = 8;
        updatePins(zoom, bingMap.current);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef]);

  useEffect(() => {
    if (bingMap.current) {
      const zoom = 8;
      updatePins(zoom, bingMap.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealers]);

  return (
    <>
      {!hideSearchThisAreaButton && (
        <LinkButton
          variant={isHondaThemed ? LinkButtonType.filled : LinkButtonType.black}
          onClick={handleSearchThisArea}
          classNames={{
            container: `${classes.searchThisAreaButton}`,
            ...(theme.themeName === 'honda' && { font: 'atm-caption-7' }),
          }}
          color={honda_blue}
          rounded={isHondaThemed}
        >
          <TextRender text={labels.searchArea} />
        </LinkButton>
      )}
      <div ref={mapRef} className={`${classes.root} dealer-map`}></div>
    </>
  );
}
